import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  OAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import {
  getFirestore,
  Firestore,
  doc,
  getDoc,
} from 'firebase/firestore';
import {
  getFunctions,
  Functions,
  httpsCallable,
} from 'firebase/functions';
import type {
  UserProfile,
  UserRole,
  UserAccessLevel,
} from '@/types';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  ...(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
    ? { measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID }
    : {}),
};


// Initialize Firebase
let app: FirebaseApp;
const existingApps = getApps();
if (existingApps.length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = existingApps[0]!;
}

export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const functions: Functions = getFunctions(app, 'us-central1');

// Azure AD OIDC Provider
export const azureADProvider = new OAuthProvider('oidc.microsoft');

// Multi-tenant default uses "common"; set a tenant ID only if you need to restrict login.
azureADProvider.setCustomParameters({
  tenant: import.meta.env.VITE_AZURE_AD_TENANT_ID || 'common',
});

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

// Sign in with Microsoft SSO
export const signInWithMicrosoft = async (): Promise<FirebaseUser> => {
  const result = await signInWithPopup(auth, azureADProvider);
  return result.user;
};

// Sign out
export const signOut = async (): Promise<void> => {
  await firebaseSignOut(auth);
};

// Get user profile from Firestore
export const getUserProfile = async (
  uid: string
): Promise<UserProfile | null> => {
  const userDoc = await getDoc(doc(db, 'users', uid));
  if (!userDoc.exists()) {
    return null;
  }
  const data = userDoc.data();
  return {
    uid: data.uid,
    email: data.email,
    displayName: data.displayName,
    role: data.role as UserRole,
    accessLevel: (data.accessLevel || 'STANDARD') as UserAccessLevel,
    isActive: data.isActive !== false,
    createdAt: data.createdAt?.toDate().toISOString() || new Date().toISOString(),
    updatedAt: data.updatedAt?.toDate().toISOString(),
  };
};

export const syncCurrentUserAccess = async (): Promise<{
  role: UserRole;
  accessLevel: UserAccessLevel;
}> => {
  const callable = httpsCallable(functions, 'syncCurrentUserAccess');
  const response = await callable();
  return response.data as {
    role: UserRole;
    accessLevel: UserAccessLevel;
  };
};

// Get current user's role from custom claims or Firestore
export const getCurrentUserRole = async (
  user: FirebaseUser
): Promise<UserRole | null> => {
  // Check custom claims first (set by Cloud Function)
  const token = await user.getIdTokenResult();
  if (token.claims.role) {
    return token.claims.role as UserRole;
  }

  // Fallback to Firestore
  const profile = await getUserProfile(user.uid);
  return profile?.role || null;
};

export const getCurrentUserAccessLevel = async (
  user: FirebaseUser
): Promise<UserAccessLevel | null> => {
  const token = await user.getIdTokenResult();
  if (token.claims.accessLevel) {
    return token.claims.accessLevel as UserAccessLevel;
  }

  const profile = await getUserProfile(user.uid);
  return profile?.accessLevel || null;
};

export const isCurrentUserActive = async (user: FirebaseUser): Promise<boolean> => {
  const token = await user.getIdTokenResult();
  if (typeof token.claims.isActive === 'boolean') {
    return token.claims.isActive;
  }

  const profile = await getUserProfile(user.uid);
  return profile?.isActive === true;
};

export const getNormalizedUserEmail = (user: FirebaseUser): string =>
  normalizeEmail(user.email || '');

// Auth state observer helper
export const onAuthStateChange = (
  callback: (user: FirebaseUser | null) => void
) => {
  return onAuthStateChanged(auth, callback);
};

export default app;

